/**
 * S3-Response Sheets Service
 *
 * Same interface as sheetsService, but uses the S3 response orchestrator
 * which bypasses API Gateway's 6MB limit by writing large responses to S3.
 *
 * When the Lambda response contains `data_url`, fetches data directly from S3.
 * Small responses (schema, count, distinct) are returned inline as before.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../aws-config';
import type {
  SchemaResponse,
  SearchResponse,
  DistinctResponse,
  CountResponse,
  SearchCriteria,
} from './sheetsService';

// Re-export types so consumers can import from this module
export type { SchemaResponse, SearchResponse, DistinctResponse, CountResponse, SearchCriteria };

// New API endpoint for S3 response orchestrator
const S3_ORCHESTRATOR_URL = lambdaEndpoints.sheetsS3;

class S3SheetsService {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Call the S3 response orchestrator Lambda.
   * If the response contains `data_url`, fetches the actual data from S3.
   */
  private async invokeLambda(payload: Record<string, any>): Promise<any> {
    const token = await this.getAuthToken();

    const response = await fetch(S3_ORCHESTRATOR_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API Gateway error: ${response.status}`);
    }

    const result = await response.json();

    // S3 redirect: Lambda wrote large response to S3, returned a presigned URL
    if (result.data_url) {
      try {
        const s3Data = await this.fetchFromS3(result.data_url);
        // Merge S3 data with the redirect metadata (keeps total_rows etc. from redirect)
        return { ...result, ...s3Data };
      } catch (err) {
        console.error('S3 fetch failed, using redirect metadata:', err);
        return { ...result, success: false, error: 'Failed to fetch data from S3' };
      }
    }

    return result;
  }

  /**
   * Fetch gzipped JSON from S3 presigned URL.
   * S3 object has ContentEncoding='gzip', so the browser auto-decompresses.
   * Falls back to manual DecompressionStream if auto-decompression doesn't work.
   */
  private async fetchFromS3(url: string): Promise<any> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`S3 fetch error: ${response.status}`);
    }

    // Try JSON parse directly (browser should auto-decompress gzip)
    try {
      return await response.json();
    } catch {
      // Manual decompression fallback (some browsers may not auto-decompress S3 presigned URLs)
      const blob = await response.blob();
      const DS = (globalThis as any).DecompressionStream;
      if (DS) {
        const ds = new DS('gzip');
        const decompressed = blob.stream().pipeThrough(ds);
        const text = await new Response(decompressed).text();
        return JSON.parse(text);
      }
      throw new Error('Browser does not support DecompressionStream');
    }
  }

  async getSchema(
    bucket: string,
    key: string,
    options?: { isMultipart?: boolean }
  ): Promise<SchemaResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'schema',
      ...(options?.isMultipart ? { isMultipart: true } : {}),
    });
  }

  async getCount(bucket: string, key: string): Promise<CountResponse> {
    return this.invokeLambda({ bucket, key, operation: 'count' });
  }

  async search(
    bucket: string,
    key: string,
    search: SearchCriteria,
    options: {
      select_columns?: string[];
      limit?: number;
      offset?: number;
      num_workers?: number;
      isMultipart?: boolean;
    } = {}
  ): Promise<SearchResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'search',
      search,
      select_columns: options.select_columns,
      limit: options.limit || 3000,
      offset: options.offset || 0,
      num_workers: options.num_workers || 50,
      ...(options.isMultipart ? { isMultipart: true } : {}),
    });
  }

  async searchAndLoad(
    bucket: string,
    key: string,
    search: SearchCriteria,
    options: {
      select_columns?: string[];
      limit?: number;
      offset?: number;
      num_workers?: number;
      isMultipart?: boolean;
    } = {}
  ): Promise<SearchResponse> {
    return this.search(bucket, key, search, options);
  }

  async getData(
    bucket: string,
    key: string,
    options: {
      select_columns?: string[];
      limit?: number;
      offset?: number;
      isMultipart?: boolean;
    } = {}
  ): Promise<SearchResponse> {
    // Get schema first to find the first column name (same as original sheetsService)
    const schemaResponse = await this.getSchema(bucket, key, {
      isMultipart: options.isMultipart,
    });
    if (!schemaResponse.success || !schemaResponse.schema?.length) {
      return { success: false, error: 'Failed to get schema' };
    }

    const firstColumn = schemaResponse.schema[0].name;

    return this.invokeLambda({
      bucket,
      key,
      operation: 'search',
      search: {
        column: firstColumn,
        operator: 'contains',
        value: '', // Empty value = match all
      },
      select_columns: options.select_columns,
      limit: options.limit || 3000,
      offset: options.offset || 0,
      ...(options.isMultipart ? { isMultipart: true } : {}),
    });
  }

  async getDistinct(
    bucket: string,
    key: string,
    column: string,
    limit: number = 100,
    options?: { isMultipart?: boolean }
  ): Promise<DistinctResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'distinct',
      column,
      limit,
      ...(options?.isMultipart ? { isMultipart: true } : {}),
    });
  }

  async searchAny(
    bucket: string,
    key: string,
    columns: string[],
    value: string,
    operator: string = 'contains',
    options: { limit?: number; offset?: number } = {}
  ): Promise<SearchResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'search_any',
      columns,
      value,
      operator,
      limit: options.limit || 1000,
      offset: options.offset || 0,
    });
  }

  parseS3Key(fileInfo: { s3Key?: string; fileName?: string }): {
    bucket: string;
    key: string;
  } {
    const key = fileInfo.s3Key || fileInfo.fileName || '';
    return { bucket: 'bigsplice-us-east-1', key };
  }
}

export const s3SheetsService = new S3SheetsService();
